export type Traits = Record<string, string | number | boolean | null>;
export type EventProperties = Record<string, string | number | boolean | null>;

export type InitOptions = {
  projectKey: string;
  /** Override the API origin (defaults to https://api.steppr.io). */
  apiUrl?: string;
  /** Disable auto route-change detection (default: false). */
  disableRouteTracking?: boolean;
};

export type EditModeState = {
  enabled: boolean;
  authToken: string | null;
};

// Mirrors packages/shared types. Kept inline here so the SDK has no workspace
// dependencies and can be shipped as a self-contained bundle.

export type StepType = 'tooltip' | 'modal' | 'hotspot';
export type StepButtonKind = 'next' | 'prev' | 'dismiss';
export type StepButton = { kind: StepButtonKind; label: string };
export type StepAdvanceMode = 'button' | 'anchor-click';

// Mirrors `StepRequirement` in packages/shared/src/types.ts. See the
// comments there for semantics.
export type StepRequirement =
  | { type: 'anchor_has_value' }
  | { type: 'event'; name: string };

export type StepConfig = {
  buttons?: StepButton[];
  showButtons?: boolean;
  advanceOn?: StepAdvanceMode;
  requireToAdvance?: StepRequirement | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  imagePosition?: 'top' | 'bottom';
  imageHref?: string | null;
  imageHrefTarget?: 'self' | 'blank';
  [key: string]: unknown;
};

export type TourSnapshotStep = {
  id: string;
  order: number;
  type: StepType;
  selector?: string | null;
  title?: string | null;
  body?: string | null;
  config?: StepConfig;
};

export type TargetingRule =
  | { type: 'url_match'; pattern: string; match: 'exact' | 'contains' | 'regex' }
  | { type: 'trait'; key: string; op: 'eq' | 'neq' | 'contains' | 'exists'; value?: string };

export type TriggerRule =
  | { type: 'page_load' }
  | { type: 'url_match'; pattern: string; match: 'exact' | 'contains' | 'regex' }
  | { type: 'event'; name: string };

// Mirrors `OnCompleteAnimation` / `FlowConfig` from packages/shared. Kept
// inline so the SDK stays workspace-dep-free.
export type OnCompleteAnimation = 'none' | 'confetti';
export type FlowConfig = Record<string, unknown> & {
  onCompleteAnimation?: OnCompleteAnimation;
  dismissOnBackdropClick?: boolean;
  disableManualCompletion?: boolean;
  bannerPlacement?: 'overlay' | 'pushdown' | 'inline';
  ctaTarget?: 'self' | 'blank';
  hideFromResourceCenter?: boolean;
};

export type TourSnapshot = {
  version: number;
  publishedAt: string;
  steps: TourSnapshotStep[];
  targeting: TargetingRule[];
  triggers: TriggerRule[];
  config?: FlowConfig;
  // Resolved per-flow theme baked at publish - see Theme below.
  theme?: Theme;
  // Optional chain pointer; when set, runtime fires the named tour after
  // this one completes (subject to the next tour's targeting + triggers).
  nextTourId?: string | null;
};

export type TourState = {
  /** 0-based step index the user reached on a previous session, or null
   *  if they haven't started this tour. The SDK uses this to resume after
   *  a full page navigation that wiped the in-memory runtime. */
  inProgressStepIndex: number | null;
  /** True if the user dismissed this tour previously. The runtime skips
   *  these in pickTour so they don't auto-re-fire, but they're kept in the
   *  list so the Resource Center can offer to relaunch them. */
  dismissed?: boolean;
  /** True if the user completed this tour previously. Same dual purpose
   *  as `dismissed`. */
  completed?: boolean;
};

export type Tour = {
  id: string;
  name: string;
  snapshot: TourSnapshot;
  /** Optional - older /v1/session payloads may not include it. */
  state?: TourState;
};

export type TourAction =
  | 'view'
  | 'start'
  | 'step_view'
  | 'step_complete'
  | 'dismiss'
  | 'complete';

// Mirrors @steppr/shared Theme. The SDK resolves defaults locally too so a
// malformed payload from the API doesn't break rendering.
export type Theme = {
  primaryColor: string;
  radius: 'sm' | 'md' | 'lg';
  hideBranding: boolean;
  textAlign: 'left' | 'center';
  buttonsAlign: 'left' | 'center' | 'right';
  fontFamily: 'system' | 'inherit';
  fontSize: 'compact' | 'default' | 'roomy';
};

// ---------- Checklist types ----------

export type ChecklistItemActionType = 'tour' | 'url' | 'event';

export type ChecklistSnapshotItem = {
  id: string;
  order: number;
  title: string;
  description?: string | null;
  actionType: ChecklistItemActionType;
  actionValue: string;
};

export type ChecklistSnapshot = {
  version: number;
  publishedAt: string;
  items: ChecklistSnapshotItem[];
  // Optional targeting rules - same shape as tour targeting. Empty / missing
  // means "show to everyone matching no filters".
  targeting?: TargetingRule[];
  config?: FlowConfig;
  theme?: Theme;
};

// ===== Announcement types =====
// Mirrors `AnnouncementDisplay`, `AnnouncementSnapshot`, `Announcement` in
// packages/shared/src/types.ts. SDK is workspace-dep-free, so types are
// duplicated.

export type AnnouncementDisplay = 'modal' | 'banner';

export type AnnouncementSnapshot = {
  version: number;
  publishedAt: string;
  display: AnnouncementDisplay;
  title?: string | null;
  body?: string | null;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  secondaryCtaLabel?: string | null;
  secondaryCtaUrl?: string | null;
  targeting: TargetingRule[];
  triggers: TriggerRule[];
  config?: FlowConfig;
  theme?: Theme;
};

export type Announcement = {
  id: string;
  name: string;
  snapshot: AnnouncementSnapshot;
  state?: { dismissed: boolean };
};

export type AnnouncementAction = 'view' | 'dismiss' | 'cta_click';

export type ChecklistStateItem = {
  completedAt: string;
  source: 'auto' | 'manual';
};

export type ChecklistState = {
  items: Record<string, ChecklistStateItem>;
  dismissedAt: string | null;
};

export type Checklist = {
  id: string;
  name: string;
  snapshot: ChecklistSnapshot;
  state: ChecklistState;
};

export const DEFAULT_THEME: Theme = {
  primaryColor: '#0f172a',
  radius: 'md',
  hideBranding: false,
  textAlign: 'left',
  buttonsAlign: 'right',
  fontFamily: 'system',
  fontSize: 'default',
};

// ===== Resource Center types =====
// Mirrors `ResourceCenterConfig` in packages/shared/src/resource-center.ts.
// SDK is workspace-dep-free, so types are duplicated.

export type ResourceCenterPosition = 'bottom-right' | 'bottom-left';

export type ResourceCenterLink = {
  id: string;
  label: string;
  url: string;
};

export type ResourceCenterSections = {
  tours: { visible: boolean };
  checklists: { visible: boolean };
  announcements: { visible: boolean; limit: number };
  links: ResourceCenterLink[];
};

export type ResourceCenterConfig = {
  enabled: boolean;
  position: ResourceCenterPosition;
  helpText?: string | null;
  title?: string | null;
  sections: ResourceCenterSections;
};

